import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { CalendarioDia, PedidoVendaResponse, StatusPedido } from '@triade/contracts';
import { ChevronLeft, ChevronRight, Copy, Pencil, Search, Trash2 } from 'lucide-react';
import { useCalendarioMes, useCarteira, useExcluirPedido, useResumoMes } from '@/api/plano';
import { usePode } from '@/api/me';
import { BarraDeAcoesLista, TAMANHOS_PAGINA, TAMANHO_PAGINA_PADRAO } from '@/components/barra-lista';
import { ErroLista } from '@/components/cargos';
import { BotaoExportar, ErroExportacao, useExportar } from '@/components/exportar';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDebounced } from '@/hooks/use-debounced';
import { ApiError } from '@/lib/api';
import { formatarContagem, formatarData, formatarDecimal, formatarFracaoPercentual, formatarMoeda } from '@/lib/format';
import { useSessao } from '@/lib/session';
import { cn } from '@/lib/utils';
import { BotaoRelatorios } from '@/relatorios';
import { type AbaCarteira, deslocarMes, rotuloMes } from './carteira-mes';
import { PedidoDialog, RepetirPedidoDialog } from './pedido-dialog';
import { rotuloStatus, STATUS_PEDIDO_ORDEM } from './pedido-form';
import { UsaAlimenta } from './usa-alimenta';

export { ABAS_CARTEIRA, lerBuscaCarteira, mesAtual, type AbaCarteira } from './carteira-mes';

const CAMPO_FILTRO = 'h-[34px] rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none';
const BOTAO_MES = 'flex size-[34px] items-center justify-center rounded-[9px] border border-border bg-card hover:bg-line-soft';
const BOTAO_ACAO = 'flex size-[30px] items-center justify-center rounded-[8px] text-ink-secondary hover:bg-line-soft hover:text-ink-body';

/**
 * Carteira de pedidos (/plano/carteira): pedidos do mes, resumo do mes e calendario por semana.
 * Nenhum valor e calculado aqui: R$, MC e resultados vem prontos da API (ausentes = "—").
 */
export function CarteiraPage({ mes, aba = 'pedidos' }: { mes: string; aba?: AbaCarteira }) {
  const { empresaId } = useSessao();
  const navigate = useNavigate();
  const mudar = (patch: { mes?: string; aba?: AbaCarteira }) => void navigate({ to: '/plano/carteira', search: { mes, aba, ...patch }, replace: true });

  return (
    <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-small text-ink-muted">
            Plano <span aria-hidden="true">/</span> Carteira de pedidos
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Carteira de pedidos</h1>
          <p className="text-small text-ink-secondary">Pedidos de venda por embarque, resumo do mês e calendário de entregas</p>
          <UsaAlimenta
            usa={[{ rotulo: 'Orçamentos', to: '/vender/orcamentos' }]}
            alimenta={[
              { rotulo: 'Necessidade de MP', to: '/plano/necessidade' },
              { rotulo: 'Custo fábrica', to: '/plano/custo-fabrica' },
              { rotulo: 'Fluxo de caixa', to: '/plano/fluxo' },
            ]}
          />
        </div>
        <nav aria-label="Mês da carteira" className="flex items-center gap-2">
          <button type="button" aria-label="Mês anterior" className={BOTAO_MES} onClick={() => mudar({ mes: deslocarMes(mes, -1) })}>
            <ChevronLeft aria-hidden="true" className="size-4" />
          </button>
          <span aria-live="polite" className="min-w-[90px] text-center text-body font-semibold tabular-nums" data-testid="mes-carteira">
            {rotuloMes(mes)}
          </span>
          <button type="button" aria-label="Próximo mês" className={BOTAO_MES} onClick={() => mudar({ mes: deslocarMes(mes, 1) })}>
            <ChevronRight aria-hidden="true" className="size-4" />
          </button>
        </nav>
      </div>

      {empresaId === null ? (
        <p role="status" className="px-5 py-10 text-center text-small text-ink-muted">Escolha a empresa no menu lateral para ver a carteira.</p>
      ) : (
        <Tabs value={aba} onValueChange={(v) => mudar({ aba: v as AbaCarteira })} className="gap-4">
          <TabsList className="flex h-auto flex-wrap justify-start gap-1">
            <TabsTrigger value="pedidos">Pedidos</TabsTrigger>
            <TabsTrigger value="resumo">Resumo</TabsTrigger>
            <TabsTrigger value="calendario">Calendário</TabsTrigger>
          </TabsList>
          <TabsContent value="pedidos">
            <AbaPedidos mes={mes} />
          </TabsContent>
          <TabsContent value="resumo">
            <AbaResumo mes={mes} />
          </TabsContent>
          <TabsContent value="calendario">
            <AbaCalendario mes={mes} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

// ---------- Aba Pedidos ----------

function AbaPedidos({ mes }: { mes: string }) {
  const { pode } = usePode();
  const podeEditar = pode('carteira.editar');
  const [termo, setTermo] = useState('');
  const termoAtrasado = useDebounced(termo.trim(), 300);
  const [status, setStatus] = useState<StatusPedido | ''>('');
  // Paginacao do servidor (S36-D3): a API devolve o total do filtro; trocar filtro volta para a pagina 1.
  const [pagina, setPagina] = useState(1);
  const [tamanho, setTamanho] = useState<number>(TAMANHO_PAGINA_PADRAO);
  useEffect(() => setPagina(1), [mes, status, termoAtrasado]);
  const lista = useCarteira({
    mes,
    limit: tamanho,
    offset: (pagina - 1) * tamanho,
    ...(status ? { status } : {}),
    ...(termoAtrasado ? { q: termoAtrasado } : {}),
  });
  const exportacao = useExportar();
  const excluir = useExcluirPedido();
  const [editando, setEditando] = useState<PedidoVendaResponse | 'novo' | null>(null);
  const [repetindo, setRepetindo] = useState<PedidoVendaResponse | null>(null);
  const [excluindo, setExcluindo] = useState<PedidoVendaResponse | null>(null);
  const [erroExclusao, setErroExclusao] = useState<string | null>(null);
  const pedidos = lista.data?.itens ?? [];

  async function confirmarExclusao() {
    if (!excluindo) return;
    setErroExclusao(null);
    try {
      await excluir.mutateAsync({ id: excluindo.id });
      setExcluindo(null);
    } catch (e) {
      setErroExclusao(e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');
    }
  }

  let corpo: ReactNode;
  if (lista.isError && !lista.data) corpo = <div className="p-4"><ErroLista erro={lista.error} onTentar={() => void lista.refetch()} titulo="Não foi possível carregar os pedidos" /></div>;
  else if (!lista.data) corpo = <Mensagem papel="status">Carregando pedidos…</Mensagem>;
  else if (pedidos.length === 0) corpo = <Mensagem papel="status">{termoAtrasado || status ? 'Nenhum pedido com esses filtros.' : 'Nenhum pedido neste mês. Os pedidos nascem de orçamentos aprovados (Gerar pedido) ou de "Novo pedido" aqui.'}</Mensagem>;
  else {
    corpo = (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1100px] border-collapse text-small" aria-label="Pedidos de venda do mês">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="px-4 py-2.5 font-medium">Nº</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Embarque</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Cliente</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Produto</th>
              <th scope="col" className="px-3 py-2.5 text-right font-medium">Qtd (t)</th>
              <th scope="col" className="px-3 py-2.5 text-right font-medium">Preço</th>
              <th scope="col" className="px-3 py-2.5 text-right font-medium">Total</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Status</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Frete</th>
              <th scope="col" className="px-3 py-2.5 text-right font-medium">MC</th>
              <th scope="col" className="px-3 py-2.5 text-right font-medium">MC/t</th>
              {podeEditar && <th scope="col" className="px-3 py-2.5 font-medium"><span className="sr-only">Ações</span></th>}
            </tr>
          </thead>
          <tbody>
            {pedidos.map((p) => (
              <tr key={p.id} data-testid="linha-pedido" className="border-t border-line-soft">
                <td className="px-4 py-3 font-mono font-semibold">{p.numero}</td>
                <td className="px-3 py-3 tabular-nums">{formatarData(p.dataEmbarque)}</td>
                <td className="px-3 py-3">{p.clienteNome}</td>
                <td className="px-3 py-3 text-ink-secondary">
                  <span className="font-mono">{p.itemCodigo}</span> {p.itemDescricao}
                </td>
                <td className="px-3 py-3 text-right tabular-nums">{formatarDecimal(p.quantidade, 0)}</td>
                <td className="px-3 py-3 text-right tabular-nums">{formatarMoeda(p.precoUnitario)}</td>
                <td className="px-3 py-3 text-right tabular-nums">{formatarMoeda(p.total)}</td>
                <td className="px-3 py-3">
                  <BadgeStatus status={p.status} />
                </td>
                <td className="px-3 py-3">{p.modalidadeFrete ?? '—'}</td>
                {p.semCusto ? (
                  <td colSpan={2} className="px-3 py-3 text-right">
                    <SeloSemCusto />
                  </td>
                ) : (
                  <>
                    <td className="px-3 py-3 text-right tabular-nums">{formatarMoeda(p.mc)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{formatarMoeda(p.mcPorUnidade)}</td>
                  </>
                )}
                {podeEditar && (
                  <td className="px-3 py-3">
                    <div className="flex justify-end gap-1">
                      <button type="button" aria-label={`Editar pedido ${p.numero}`} className={BOTAO_ACAO} onClick={() => setEditando(p)}>
                        <Pencil aria-hidden="true" className="size-4" />
                      </button>
                      <button type="button" aria-label={`Repetir pedido ${p.numero}`} className={BOTAO_ACAO} onClick={() => setRepetindo(p)}>
                        <Copy aria-hidden="true" className="size-4" />
                      </button>
                      <button type="button" aria-label={`Excluir pedido ${p.numero}`} className={cn(BOTAO_ACAO, 'hover:text-danger-ink')} onClick={() => { setErroExclusao(null); setExcluindo(p); }}>
                        <Trash2 aria-hidden="true" className="size-4" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <section aria-label="Pedidos do mês" aria-busy={lista.isFetching} className="rounded-2xl border border-border bg-card">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3.5">
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="pedido-status-filtro" className="text-caption font-medium text-ink-secondary">Status</label>
          <select id="pedido-status-filtro" className={CAMPO_FILTRO} value={status} onChange={(e) => setStatus(e.target.value as StatusPedido | '')}>
            <option value="">Todos</option>
            {STATUS_PEDIDO_ORDEM.map((s) => (
              <option key={s} value={s}>
                {rotuloStatus(s)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex h-[34px] w-[260px] max-w-full items-center gap-2 rounded-[9px] border border-input bg-card px-2.5 focus-within:border-primary focus-within:shadow-focus">
          <Search aria-hidden="true" className="size-[15px] text-ink-muted" />
          <input id="pedido-busca" type="search" aria-label="Buscar pedido" value={termo} onChange={(e) => setTermo(e.target.value)} placeholder="Cliente, produto ou nº" autoComplete="off" className="w-full min-w-0 bg-transparent text-small outline-none" />
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <BotaoExportar rotulo="Exportar XLSX" exportando={exportacao.exportando} onClick={() => void exportacao.exportar('/carteira/exportar.xlsx', { mes })} />
        </div>
      </div>
      <ErroExportacao erro={exportacao.erro} />
      {corpo}
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3 text-small text-ink-secondary" data-testid="contagem-pedidos">
        {lista.data ? (
          <>
            <span>
              {formatarContagem(lista.data.total)} {lista.data.total === 1 ? 'pedido' : 'pedidos'}
              {lista.data.total > pedidos.length ? ` (mostrando ${formatarContagem(pedidos.length)})` : ''}
            </span>
            <span className="tabular-nums">Total no filtro: {formatarDecimal(lista.data.somaQuantidade, 0)} t</span>
          </>
        ) : (
          ' '
        )}
      </div>

      <BarraDeAcoesLista
        novo={podeEditar ? { rotulo: 'Novo pedido', onClick: () => setEditando('novo') } : undefined}
        paginacao={
          lista.data
            ? { pagina, tamanho, total: lista.data.total, onPagina: setPagina, onTamanho: (t) => { setTamanho(t); setPagina(1); }, tamanhos: [...TAMANHOS_PAGINA] }
            : undefined
        }
        relatorios={<BotaoRelatorios tela="carteira" params={{ mes, status: status || undefined, q: termoAtrasado || undefined }} />}
      />

      {editando !== null && <PedidoDialog key={editando === 'novo' ? 'novo' : editando.id} pedido={editando === 'novo' ? null : editando} mes={mes} onFechar={() => setEditando(null)} />}
      {repetindo !== null && <RepetirPedidoDialog key={repetindo.id} pedido={repetindo} onFechar={() => setRepetindo(null)} />}
      <AlertDialog open={excluindo !== null} onOpenChange={(o) => !o && !excluir.isPending && setExcluindo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir pedido nº {excluindo?.numero}?</AlertDialogTitle>
            <AlertDialogDescription>O pedido sai da carteira e do plano de compras e do fluxo deste mês.</AlertDialogDescription>
          </AlertDialogHeader>
          {erroExclusao && (
            <p role="alert" className="text-small text-danger-ink">
              Não foi possível excluir: {erroExclusao}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={excluir.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={excluir.isPending}
              onClick={(e) => {
                e.preventDefault();
                void confirmarExclusao();
              }}
            >
              {excluir.isPending ? 'Excluindo…' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

// ---------- Aba Resumo ----------

function AbaResumo({ mes }: { mes: string }) {
  const resumo = useResumoMes(mes);

  if (resumo.isError && !resumo.data) return <div className="p-4"><ErroLista erro={resumo.error} onTentar={() => void resumo.refetch()} titulo="Não foi possível carregar o resumo" /></div>;
  if (!resumo.data) return <Mensagem papel="status">Carregando resumo…</Mensagem>;
  const r = resumo.data;

  return (
    <div className="flex flex-col gap-5">
      <section aria-label="Totais do mês" className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Cartao rotulo="Faturamento" valor={formatarMoeda(r.faturamento)} />
        <Cartao rotulo="Margem de contribuição (MC)" valor={formatarMoeda(r.mc)} />
        <Cartao rotulo="MC por tonelada" valor={formatarMoeda(r.mcPorUnidade)} nota="Sobre as toneladas com custo." />
        <Cartao rotulo="Volume (t)" valor={formatarDecimal(r.quantidade, 0)} />
        <Cartao rotulo="Custo fábrica" valor={formatarMoeda(r.custoFabrica)} />
      </section>

      {r.pedidosSemCusto > 0 && (
        <p role="note" className="text-caption text-ink-secondary">
          {`${formatarContagem(r.pedidosSemCusto)} ${r.pedidosSemCusto === 1 ? 'pedido sem custo fica' : 'pedidos sem custo ficam'} fora da MC e do resultado (${formatarDecimal(r.quantidadeSemCusto, 0)} t). Faturamento e toneladas contam.`}
        </p>
      )}

      <section aria-label="Resultado do mês" className="grid gap-3 md:grid-cols-2">
        <Cartao rotulo="Resultado como a planilha" valor={formatarMoeda(r.resultadoPlanilha)} nota="MC menos o custo fábrica, com todas as rubricas (inclusive as de caixa)." destaque />
        <Cartao rotulo="Resultado gerencial" valor={formatarMoeda(r.resultadoGerencial)} nota="As rubricas de caixa saem deste resultado (vão só para o fluxo de caixa)." destaque />
      </section>

      <p className="text-caption text-ink-muted">Peso da Previsão no total do mês: {formatarFracaoPercentual(r.pesoPrevisao)}. Pedidos pendentes ficam fora do total.</p>

      <div className="grid gap-5 xl:grid-cols-2">
        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full min-w-[480px] border-collapse text-small" aria-label="Resumo por status">
            <thead>
              <tr className="text-left text-caption text-ink-muted">
                <th scope="col" className="px-4 py-2.5 font-medium">Status</th>
                <th scope="col" className="px-3 py-2.5 text-right font-medium">Pedidos</th>
                <th scope="col" className="px-3 py-2.5 text-right font-medium">Volume (t)</th>
                <th scope="col" className="px-3 py-2.5 text-right font-medium">Faturamento</th>
                <th scope="col" className="px-3 py-2.5 text-right font-medium">MC</th>
              </tr>
            </thead>
            <tbody>
              {r.porStatus.map((l) => (
                <tr key={l.status} className="border-t border-line-soft">
                  <td className="px-4 py-3"><BadgeStatus status={l.status} /></td>
                  <td className="px-3 py-3 text-right tabular-nums">{formatarContagem(l.pedidos)}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{formatarDecimal(l.quantidade, 0)}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{formatarMoeda(l.faturamento)}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{formatarMoeda(l.mc)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full min-w-[480px] border-collapse text-small" aria-label="Resumo por cliente">
            <thead>
              <tr className="text-left text-caption text-ink-muted">
                <th scope="col" className="px-4 py-2.5 font-medium">Cliente</th>
                <th scope="col" className="px-3 py-2.5 text-right font-medium">Pedidos</th>
                <th scope="col" className="px-3 py-2.5 text-right font-medium">Volume (t)</th>
                <th scope="col" className="px-3 py-2.5 text-right font-medium">Faturamento</th>
                <th scope="col" className="px-3 py-2.5 text-right font-medium">MC</th>
              </tr>
            </thead>
            <tbody>
              {r.porCliente.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-ink-muted">Nenhum cliente no mês.</td>
                </tr>
              ) : (
                r.porCliente.map((l) => (
                  <tr key={l.clienteId} className="border-t border-line-soft">
                    <td className="px-4 py-3">{l.clienteNome}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{formatarContagem(l.pedidos)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{formatarDecimal(l.quantidade, 0)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{formatarMoeda(l.faturamento)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{formatarMoeda(l.mc)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Cartao({ rotulo, valor, nota, destaque = false }: { rotulo: string; valor: string; nota?: string; destaque?: boolean }) {
  return (
    <div className={cn('rounded-xl border border-border bg-card p-4', destaque && 'bg-surface-muted')}>
      <div className="text-caption text-ink-muted">{rotulo}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums">{valor}</div>
      {nota && <p className="mt-1 text-caption text-ink-secondary">{nota}</p>}
    </div>
  );
}

// ---------- Aba Calendario ----------

const DIAS_SEMANA = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

function AbaCalendario({ mes }: { mes: string }) {
  const calendario = useCalendarioMes(mes);

  if (calendario.isError && !calendario.data) return <div className="p-4"><ErroLista erro={calendario.error} onTentar={() => void calendario.refetch()} titulo="Não foi possível carregar o calendário" /></div>;
  if (!calendario.data) return <Mensagem papel="status">Carregando calendário…</Mensagem>;
  const semanas = calendario.data.semanas;
  if (semanas.length === 0) return <Mensagem papel="status">Nenhum embarque neste mês.</Mensagem>;

  return (
    <div className="overflow-x-auto rounded-2xl border border-border bg-card">
      <table className="w-full min-w-[900px] table-fixed border-collapse text-small" aria-label="Calendário de embarques por semana">
        <thead>
          <tr className="text-left text-caption text-ink-muted">
            <th scope="col" className="w-[90px] px-3 py-2.5 font-medium">Semana</th>
            {DIAS_SEMANA.map((d) => (
              <th key={d} scope="col" className="px-2 py-2.5 font-medium">{d}</th>
            ))}
            <th scope="col" className="w-[130px] px-3 py-2.5 text-right font-medium">Total da semana</th>
          </tr>
        </thead>
        <tbody>
          {semanas.map((s) => (
            <tr key={s.semana} className="border-t border-line-soft align-top">
              <th scope="row" className="px-3 py-3 text-left font-semibold">Semana {s.semana}</th>
              {DIAS_SEMANA.map((_, i) => {
                const dia = s.dias.find((d) => d.diaSemana === i + 1);
                return (
                  <td key={i} className="px-1.5 py-2">
                    {dia && <CelulaDia dia={dia} />}
                  </td>
                );
              })}
              <td className="px-3 py-3 text-right">
                <div className="font-semibold tabular-nums">{formatarDecimal(s.quantidade, 0)} t</div>
                {s.faturamento !== undefined && <div className="text-caption text-ink-secondary tabular-nums">{formatarMoeda(s.faturamento)}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CelulaDia({ dia }: { dia: CalendarioDia }) {
  return (
    <div className="flex min-h-[64px] flex-col gap-1.5 rounded-lg bg-surface-muted/50 p-1.5">
      <div className="flex items-center justify-between text-caption text-ink-muted">
        <span className="tabular-nums">{formatarData(dia.data)}</span>
        <span className="font-semibold tabular-nums">{formatarDecimal(dia.quantidade, 0)} t</span>
      </div>
      {dia.pedidos.map((p) => (
        <div key={p.id} data-testid="chip-pedido" className="flex flex-col gap-0.5 rounded-md border border-border bg-card px-1.5 py-1 text-caption">
          <span className="font-medium">
            #{p.numero} {p.clienteNome}
          </span>
          <span className="text-ink-secondary">
            {p.itemCodigo} · {formatarDecimal(p.quantidade, 0)} t
          </span>
          <BadgeStatus status={p.status} />
        </div>
      ))}
    </div>
  );
}

// ---------- Comuns ----------

const CLASSE_STATUS: Record<StatusPedido, string> = {
  CONCLUIDO: 'bg-success-bg text-success-ink',
  CONFIRMADO: 'bg-line-soft text-ink-body',
  PREVISAO: 'bg-warning-bg text-warning-ink',
  PENDENTE: 'bg-surface-muted text-ink-muted',
};

function SeloSemCusto() {
  return (
    <span title="Fora da MC e do resultado do mês" className="inline-block rounded-full bg-surface-muted px-2 py-0.5 text-caption font-semibold whitespace-nowrap text-ink-secondary">
      Sem custo
    </span>
  );
}

function BadgeStatus({ status }: { status: StatusPedido }) {
  return <span className={cn('inline-block rounded-full px-2 py-0.5 text-caption font-semibold whitespace-nowrap', CLASSE_STATUS[status])}>{rotuloStatus(status)}</span>;
}

function Mensagem({ children, papel }: { children: ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}
